/**
 * Asset, Prepayment, Loan & Investment Input Dialog
 * 
 * When Bank Feed AI detects specific transaction types, this dialog
 * prompts users for additional information:
 * 
 * For Fixed Assets:
 * - Asset Category (AI-suggested)
 * - Useful Life (months) for depreciation
 * 
 * For Prepayments:
 * - Prepayment Type (AI-suggested)
 * - Coverage Period (months) for amortization
 * 
 * For Loans:
 * - Lender Name
 * - Term (months) - default 12 months
 * - Interest Rate - default 0% (user updates in Loan Schedule)
 * 
 * For Investments:
 * - Institution Name
 * - Investment Type (AI-suggested)
 * - Expected Return Rate (optional)
 * 
 * If user doesn't provide input, system uses defaults.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { FormattedNumberInput } from '../ui/formatted-number-input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { 
  Loader2, 
  Sparkles, 
  Brain, 
  CheckCircle2, 
  Building2, 
  Clock, 
  Briefcase, 
  Car,
  Monitor,
  Sofa,
  Wrench,
  Calendar,
  Shield,
  Wifi,
  Megaphone,
  FileText,
  Zap,
  Info,
  Landmark,
  TrendingUp,
  Wallet,
  PiggyBank,
  Receipt,
  Banknote
} from 'lucide-react';

// Asset categories with icons and default useful life
const ASSET_CATEGORIES = {
  computer_equipment: { label: 'Computer Equipment', icon: Monitor, defaultLife: 36 },
  office_equipment: { label: 'Office Equipment', icon: Wrench, defaultLife: 48 },
  furniture: { label: 'Furniture & Fixtures', icon: Sofa, defaultLife: 60 },
  vehicles: { label: 'Motor Vehicles', icon: Car, defaultLife: 48 },
  machinery: { label: 'Plant & Machinery', icon: Wrench, defaultLife: 60 },
  buildings: { label: 'Buildings', icon: Building2, defaultLife: 300 },
  software: { label: 'Software', icon: Monitor, defaultLife: 36 },
  tools: { label: 'Tools & Equipment', icon: Wrench, defaultLife: 48 },
  leasehold: { label: 'Leasehold Improvements', icon: Building2, defaultLife: 60 }
};

// Prepayment types with icons and default coverage
const PREPAYMENT_TYPES = {
  rent: { label: 'Rent', icon: Building2, defaultMonths: 12 },
  insurance: { label: 'Insurance', icon: Shield, defaultMonths: 12 },
  subscription: { label: 'Subscriptions', icon: Wifi, defaultMonths: 12 },
  maintenance: { label: 'Maintenance', icon: Wrench, defaultMonths: 12 },
  advertising: { label: 'Advertising', icon: Megaphone, defaultMonths: 6 },
  professional_services: { label: 'Professional Services', icon: Briefcase, defaultMonths: 12 },
  utilities: { label: 'Utilities', icon: Zap, defaultMonths: 3 },
  other: { label: 'Other', icon: FileText, defaultMonths: 12 }
};

// Loan types
const LOAN_TYPES = {
  bank_loan: { label: 'Bank Loan', icon: Landmark },
  director_loan: { label: 'Director Loan', icon: Briefcase },
  shareholder_loan: { label: 'Shareholder Loan', icon: Briefcase },
  mortgage: { label: 'Mortgage', icon: Building2 },
  vehicle_loan: { label: 'Vehicle Loan', icon: Car },
  equipment_loan: { label: 'Equipment Loan', icon: Wrench },
  other: { label: 'Other Loan', icon: Banknote }
};

// Investment types
const INVESTMENT_TYPES = {
  fixed_deposit: { label: 'Fixed Deposit', icon: PiggyBank },
  treasury_bill: { label: 'Treasury Bill', icon: Receipt },
  stocks: { label: 'Stocks/Shares', icon: TrendingUp },
  bonds: { label: 'Bonds', icon: FileText },
  mutual_fund: { label: 'Mutual Fund', icon: Wallet },
  real_estate: { label: 'Real Estate', icon: Building2 },
  // ── Phase 3 (Feb 2026) - Loans Receivable (Loan Given). The business
  // lends money to a borrower and tracks it as a monetary investment.
  // Each variant resolves to a borrower-type-specific asset GL:
  //   employee → 1140, director → 1150, related party → 1160,
  //   inter-company → 1865, third-party → 1180, customer → 1110.
  employee_loan_given: { label: 'Loan Given - Employee', icon: Briefcase },
  director_loan_given: { label: 'Loan Given - Director', icon: Briefcase },
  related_party_loan_given: { label: 'Loan Given - Related Party', icon: Briefcase },
  inter_company_loan_given: { label: 'Loan Given - Inter-Company', icon: Building2 },
  third_party_loan_given: { label: 'Loan Given - Third Party', icon: Banknote },
  customer_loan_given: { label: 'Loan Given - Customer', icon: Banknote },
  other: { label: 'Other Investment', icon: Briefcase }
};

export const AssetPrepaymentInputDialog = ({
  open,
  onOpenChange,
  transaction,
  classificationType, // 'fixed_asset', 'prepayment', 'loan', 'investment'
  onSubmit,
  country = 'NG'
}) => {
  const [loading, setLoading] = useState(false);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  
  // Fixed Asset state
  const [assetCategory, setAssetCategory] = useState('');
  const [usefulLifeMonths, setUsefulLifeMonths] = useState('');
  
  // Prepayment state
  const [prepaymentType, setPrepaymentType] = useState('');
  const [coverageMonths, setCoverageMonths] = useState('');
  
  // Loan state
  const [lenderName, setLenderName] = useState('');
  const [loanTermMonths, setLoanTermMonths] = useState('12');
  const [loanType, setLoanType] = useState('bank_loan');
  
  // Investment state
  const [institutionName, setInstitutionName] = useState('');
  const [investmentType, setInvestmentType] = useState('other');
  const [expectedReturnRate, setExpectedReturnRate] = useState('');
  // Phase 3b (iter 722) - for loan_given variants the user can pick a
  // repayment schedule.  Defaults to interest-only for parity with
  // pre-existing investments.
  const [returnFrequency, setReturnFrequency] = useState('monthly');
  const [repaymentMethod, setRepaymentMethod] = useState('interest_only');
  const [maturityDate, setMaturityDate] = useState('');
  
  // Determine type
  const isFixedAsset = classificationType === 'fixed_asset' || classificationType === 'fixed_asset_purchase';
  const isPrepayment = classificationType === 'prepayment' || classificationType === 'supplier_prepayment';
  const isLoan = classificationType === 'loan' || classificationType === 'loan_received';
  // iter921k - `capital_contribution` is OWNER'S EQUITY, not a tracked
  // investment. It must not surface this dialog (which collects fair-
  // value / dividend tracking data). Removed from isInvestment.
  const isInvestment = classificationType === 'investment' || classificationType === 'investment_made';
  
  // Fetch AI suggestion when dialog opens
  useEffect(() => {
    if (open && transaction) {
      // Reset states
      setLenderName(transaction.counterparty || '');
      setInstitutionName(transaction.counterparty || '');
      // Phase 3 (Feb 2026) - honour the suggested investment_type
      // (e.g. loan_given variants from the SpecialItemDecisionModal flow).
      if (transaction.suggested_investment_type) {
        setInvestmentType(transaction.suggested_investment_type);
      }
      fetchAISuggestion();
    }
    // iter 747 - depend on stable primitive transaction.id, not the
    // transaction object reference, to avoid infinite refetch loops when
    // the parent rebuilds the transaction prop on every render.
  }, [open, transaction?.id, transaction?.description]);
  
  const fetchAISuggestion = async () => {
    if (!transaction?.description) return;
    
    setAiSuggesting(true);
    try {
      if (isFixedAsset) {
        const res = await axios.post(`${API}/assets/suggest-category`, {
          description: transaction.description,
          vendor_name: transaction.counterparty || '',
          amount: transaction.amount
        });
        
        const data = res.data;
        setSuggestion(data);
        setAssetCategory(data.category_key || 'office_equipment');
        setUsefulLifeMonths(data.useful_life_months?.toString() || '48');
      } else if (isPrepayment) {
        const res = await axios.post(`${API}/prepayments/suggest-type`, {
          description: transaction.description,
          vendor_name: transaction.counterparty || '',
          amount: transaction.amount
        });
        
        const data = res.data;
        setSuggestion(data);
        setPrepaymentType(data.prepayment_type || 'other');
        setCoverageMonths(data.suggested_months?.toString() || '12');
      } else if (isLoan) {
        // No AI suggestion for loans - just set defaults
        setLenderName(transaction.counterparty || '');
        setLoanTermMonths('12');
        setLoanType('bank_loan');
      } else if (isInvestment) {
        // No AI suggestion for investments - just set defaults
        setInstitutionName(transaction.counterparty || '');
        setInvestmentType('other');
        setExpectedReturnRate('');
      }
    } catch (error) {
      console.log('AI suggestion error:', error);
      // Set defaults if AI fails
      if (isFixedAsset) {
        setAssetCategory('office_equipment');
        setUsefulLifeMonths('48');
      } else if (isPrepayment) {
        setPrepaymentType('other');
        setCoverageMonths('12');
      }
    } finally {
      setAiSuggesting(false);
    }
  };
  
  const handleSubmit = async () => {
    setLoading(true);
    try {
      let userInputs = {};
      
      if (isFixedAsset) {
        userInputs = {
          asset_category: assetCategory,
          useful_life_months: parseInt(usefulLifeMonths) || 48
        };
      } else if (isPrepayment) {
        userInputs = {
          prepayment_type: prepaymentType,
          coverage_months: parseInt(coverageMonths) || 12
        };
      } else if (isLoan) {
        userInputs = {
          lender_name: lenderName || transaction.counterparty || 'Lender',
          loan_term_months: parseInt(loanTermMonths) || 12,
          interest_rate: 0  // 0% by default - user updates in Loan Schedule
        };
      } else if (isInvestment) {
        const isLoanGiven = investmentType && investmentType.endsWith('_loan_given');
        userInputs = {
          institution_name: institutionName || transaction.counterparty || 'Institution',
          investment_type: investmentType,
          expected_return_rate: parseFloat(expectedReturnRate) || 0,
          // For loan_given variants the schedule is essential - pass
          // through so the backend builds the right amortisation table.
          ...(isLoanGiven ? {
            return_frequency: returnFrequency || 'monthly',
            repayment_method: repaymentMethod || 'interest_only',
            maturity_date: maturityDate || undefined,
          } : {}),
        };
      }
      
      await onSubmit(transaction, userInputs);
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSkip = () => {
    // User skips - system will use defaults
    let message = 'Using default settings.';
    if (isFixedAsset) {
      message += ' Update depreciation in Fixed Assets page.';
    } else if (isPrepayment) {
      message += ' Update amortization in Prepaid Expenses page.';
    } else if (isLoan) {
      message += ' Update interest rate in Loan Schedule page.';
    } else if (isInvestment) {
      message += ' Update returns in Investments page.';
    }
    toast.info(message);
    onSubmit(transaction, {});
    onOpenChange(false);
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };
  
  // Get dialog title and icon
  const getDialogHeader = () => {
    if (isFixedAsset) {
      return { icon: <Briefcase className="w-5 h-5 text-emerald-600" />, title: 'Fixed Asset Detected', description: 'AI detected a fixed asset purchase. Please confirm the asset category and useful life for depreciation.' };
    } else if (isPrepayment) {
      return { icon: <Clock className="w-5 h-5 text-purple-600" />, title: 'Prepaid Expense Detected', description: 'AI detected a prepaid expense. Please confirm the type and coverage period for amortization.' };
    } else if (isLoan) {
      return { icon: <Landmark className="w-5 h-5 text-blue-600" />, title: 'Loan Received', description: 'A loan has been received. Please confirm the lender and term. Interest rate defaults to 0% - update in Loan Schedule.' };
    } else if (isInvestment) {
      return { icon: <TrendingUp className="w-5 h-5 text-amber-600" />, title: 'Investment Made', description: 'An investment has been detected. Please confirm the institution and type.' };
    }
    return { icon: <FileText className="w-5 h-5" />, title: 'Transaction Detected', description: 'Please confirm the details.' };
  };
  
  const header = getDialogHeader();
  
  if (!transaction) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {header.icon}
            {header.title}
          </DialogTitle>
          <DialogDescription>
            {header.description}
          </DialogDescription>
        </DialogHeader>
        
        {/* Transaction Info */}
        <div className="p-3 bg-muted rounded-lg mb-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="font-medium">{transaction.description}</p>
              <p className="text-sm text-muted-foreground">{transaction.counterparty || 'Unknown'}</p>
              <p className="text-xs text-muted-foreground">{transaction.date}</p>
            </div>
            <div className="text-right">
              <p className={`text-lg font-bold ${isLoan ? 'text-green-600' : 'text-red-600'}`}>
                {isLoan ? '+' : '-'}{formatCurrency(transaction.amount)}
              </p>
            </div>
          </div>
        </div>
        
        {/* AI Suggestion Banner (for Fixed Assets and Prepayments) */}
        {(isFixedAsset || isPrepayment) && (
          aiSuggesting ? (
            <div className="p-3 bg-[#108981]/10 rounded-lg border border-[#108981]/20 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-[#108981]" />
              <span className="text-sm">AI analyzing transaction...</span>
            </div>
          ) : suggestion && (
            <div className="p-3 bg-[#108981]/10 rounded-lg border border-[#108981]/20">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#108981]" />
                <span className="text-sm font-medium text-[#108981]">AI Suggestion</span>
                <Badge variant="outline" className="text-[10px]">
                  {suggestion.ai_powered ? 'AI Powered' : 'Auto'}
                </Badge>
              </div>
              {isFixedAsset ? (
                <div className="text-sm space-y-1">
                  <p><strong>Category:</strong> {suggestion.category_name || ASSET_CATEGORIES[assetCategory]?.label}</p>
                  <p><strong>Asset GL:</strong> {suggestion.asset_gl_code}</p>
                  <p><strong>Depreciation GL:</strong> {suggestion.depreciation_gl_code}</p>
                  <p><strong>Suggested Life:</strong> {suggestion.useful_life_months} months</p>
                </div>
              ) : isPrepayment && (
                <div className="text-sm space-y-1">
                  <p><strong>Type:</strong> {suggestion.type_name || PREPAYMENT_TYPES[prepaymentType]?.label}</p>
                  <p><strong>Prepaid GL:</strong> {suggestion.prepaid_gl_code} - {suggestion.prepaid_gl_name}</p>
                  <p><strong>Expense GL:</strong> {suggestion.expense_gl_code} - {suggestion.expense_gl_name}</p>
                  <p><strong>Suggested Period:</strong> {suggestion.suggested_months} months</p>
                </div>
              )}
              {suggestion.reasoning && (
                <p className="text-xs text-muted-foreground mt-2 italic">{suggestion.reasoning}</p>
              )}
            </div>
          )
        )}
        
        {/* Input Fields - Fixed Asset */}
        {isFixedAsset && (
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Asset Category
                {suggestion && <Brain className="w-3 h-3 text-[#108981]" />}
              </Label>
              <Select value={assetCategory} onValueChange={setAssetCategory}>
                <SelectTrigger className={suggestion ? 'border-[#108981]/50 bg-[#108981]/5' : ''}>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ASSET_CATEGORIES).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {config.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Useful Life (Months)
                {suggestion && <Brain className="w-3 h-3 text-[#108981]" />}
              </Label>
              <Input
                type="number"
                value={usefulLifeMonths}
                onChange={(e) => setUsefulLifeMonths(e.target.value)}
                placeholder="e.g., 36"
                min="1"
                max="600"
                className={suggestion ? 'border-[#108981]/50 bg-[#108981]/5' : ''}
              />
              <p className="text-xs text-muted-foreground">
                Monthly depreciation: {formatCurrency(transaction.amount / (parseInt(usefulLifeMonths) || 1))}
              </p>
            </div>
          </div>
        )}
        
        {/* Input Fields - Prepayment */}
        {isPrepayment && (
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Prepayment Type
                {suggestion && <Brain className="w-3 h-3 text-[#108981]" />}
              </Label>
              <Select value={prepaymentType} onValueChange={setPrepaymentType}>
                <SelectTrigger className={suggestion ? 'border-[#108981]/50 bg-[#108981]/5' : ''}>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PREPAYMENT_TYPES).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {config.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Coverage Period (Months)
                {suggestion && <Brain className="w-3 h-3 text-[#108981]" />}
              </Label>
              <Input
                type="number"
                value={coverageMonths}
                onChange={(e) => setCoverageMonths(e.target.value)}
                placeholder="e.g., 12"
                min="1"
                max="60"
                className={suggestion ? 'border-[#108981]/50 bg-[#108981]/5' : ''}
              />
              <p className="text-xs text-muted-foreground">
                Monthly amortization: {formatCurrency(transaction.amount / (parseInt(coverageMonths) || 1))}
              </p>
            </div>
          </div>
        )}
        
        {/* Input Fields - Loan */}
        {isLoan && (
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Lender Name</Label>
              <Input
                value={lenderName}
                onChange={(e) => setLenderName(e.target.value)}
                placeholder="e.g., First Bank, GTBank, Director"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Loan Type</Label>
              <Select value={loanType} onValueChange={setLoanType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(LOAN_TYPES).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {config.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Loan Term (Months)</Label>
              <Input
                type="number"
                value={loanTermMonths}
                onChange={(e) => setLoanTermMonths(e.target.value)}
                placeholder="e.g., 12"
                min="1"
                max="360"
              />
              <p className="text-xs text-muted-foreground">
                Monthly principal: {formatCurrency(transaction.amount / (parseInt(loanTermMonths) || 1))} (0% interest - update in Loan Schedule)
              </p>
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Interest rate defaults to 0%. Update the actual interest rate in the Loan Schedule page after creation.
              </p>
            </div>
          </div>
        )}
        
        {/* Input Fields - Investment */}
        {isInvestment && (
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Institution Name</Label>
              <Input
                value={institutionName}
                onChange={(e) => setInstitutionName(e.target.value)}
                placeholder="e.g., Bank, Broker, Investment Company"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Investment Type</Label>
              <Select value={investmentType} onValueChange={setInvestmentType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(INVESTMENT_TYPES).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {config.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Expected Return Rate (% per annum) - Optional</Label>
              <Input
                type="number"
                value={expectedReturnRate}
                onChange={(e) => setExpectedReturnRate(e.target.value)}
                placeholder="e.g., 15"
                min="0"
                max="100"
                step="0.1"
              />
              <p className="text-xs text-muted-foreground">
                Leave blank if unknown - update in Investments page
              </p>
            </div>

            {/* Loan-Given repayment options - visible only when the
                user picked a loan_given variant.  Default schedule is
                interest-only monthly so non-related-party (third-party,
                customer) loans can be tracked with custom amortisation. */}
            {investmentType && investmentType.endsWith('_loan_given') && (
              <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
                <div className="text-xs font-semibold uppercase text-muted-foreground">
                  Loan Repayment Terms
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Repayment Method</Label>
                  <Select value={repaymentMethod} onValueChange={setRepaymentMethod}>
                    <SelectTrigger data-testid="loan-repayment-method">
                      <SelectValue placeholder="Choose method" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="interest_only">
                        Interest-only periodic + principal at maturity
                      </SelectItem>
                      <SelectItem value="equal_principal">
                        Equal principal + declining interest
                      </SelectItem>
                      <SelectItem value="equal_installment">
                        Amortising: equal payment each period
                      </SelectItem>
                      <SelectItem value="balloon">
                        Balloon: periodic interest, principal at maturity
                      </SelectItem>
                      <SelectItem value="bullet">
                        Bullet: single payment of principal + interest at maturity
                      </SelectItem>
                      <SelectItem value="custom">
                        Custom: define each repayment manually (in Loans tab)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label className="text-xs">Frequency</Label>
                    <Select value={returnFrequency} onValueChange={setReturnFrequency}>
                      <SelectTrigger data-testid="loan-frequency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="annually">Annually</SelectItem>
                        <SelectItem value="at_maturity">At maturity</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Maturity Date</Label>
                    <Input
                      type="date"
                      value={maturityDate}
                      onChange={(e) => setMaturityDate(e.target.value)}
                      data-testid="loan-maturity-date"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Choose <span className="font-semibold">custom</span> for friend/peer loans with non-standard schedules: you can edit each instalment in the Loans Receivable tab after saving.
                </p>
              </div>
            )}
          </div>
        )}
        
        {/* Info about defaults */}
        <div className="mt-4 p-2 bg-amber-50 rounded-lg border border-amber-200 flex items-start gap-2">
          <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-800">
            {isFixedAsset && 'If you skip, country-specific defaults will be used. Update depreciation settings later in Fixed Assets.'}
            {isPrepayment && 'If you skip, defaults will be used. Update amortization settings later in Prepaid Expenses.'}
            {isLoan && 'If you skip, defaults (12 months, 0% interest) will be used. Update in Loan Schedule.'}
            {isInvestment && 'If you skip, defaults will be used. Update return rate in Investments page.'}
          </p>
        </div>
        
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={handleSkip} disabled={loading}>
            Skip (Use Defaults)
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || aiSuggesting}
            className="bg-[#108981] hover:bg-[#108981]/90"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {isFixedAsset && 'Create Asset & Depreciation'}
                {isPrepayment && 'Create Prepayment & Schedule'}
                {isLoan && 'Create Loan & Schedule'}
                {isInvestment && 'Create Investment'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssetPrepaymentInputDialog;
